import { ONBOARDING_PAGE } from "@/constants";
import {
  inFacebookBrowser,
  isIOS,
  isTikTokBrowser,
  isXBrowser,
} from "@/utils/browser";

const REDIRECT_PATH_LOCAL_STORAGE_KEY = "post_login_redirect_path";
const REDIRECT_ACTION_LOCAL_STORAGE_KEY = "post_login_redirect_action";
const REDIRECT_QUERY_PARAMS_LOCAL_STORAGE_KEY =
  "post_login_redirect_query_params";

export type RedirectAction = "login" | "follow";
export type RedirectData = {
  path: string;
  action: RedirectAction;
  queryParams: Record<string, string>;
};

/**
 * Opens an external URL directly in the external browser, bypassing the redirect page
 * @param externalUrl - The external URL to open
 * @returns - Whether the redirect was successful
 */
export function openExternalUrlInBrowser(externalUrl: string): boolean {
  const isInAdBrowser =
    inFacebookBrowser() || isTikTokBrowser() || (isXBrowser() && isIOS());
  if (!isInAdBrowser) {
    return false;
  }

  if (isIOS()) {
    window.location.href = `x-safari-${externalUrl}`;
    return true;
  }

  if (/android/i.test(window.navigator.userAgent)) {
    window.location.href = `intent:${externalUrl}#Intent;end`;
    return true;
  }
  return false;
}

/**
 * Redirects to the external browser if the user is in the ad browser and on the game details page
 * @param followOnSuccess - Whether to follow the game on login success
 * @returns - Whether the redirect was successful
 */
export function redirectGameToExternalBrowser(
  followOnSuccess: boolean,
  path?: string,
  queryParams?: Record<string, string | object>,
): boolean {
  const isGameDetailsPage = /^\/games\/[^/]+(?:\/)?$/i.test(
    window.location.pathname,
  );
  const isOnboardingPage = window.location.pathname.includes(ONBOARDING_PAGE);
  const isInAdBrowser =
    inFacebookBrowser() || isTikTokBrowser() || (isXBrowser() && isIOS());
  if (!isInAdBrowser || (!isGameDetailsPage && !isOnboardingPage)) {
    return false;
  }

  const params = new URLSearchParams({
    path: path ?? window.location.pathname,
    follow: followOnSuccess.toString(),
    sessionId:
      localStorage.getItem("ajs_anonymous_id")?.replace(/"/g, "") || "",
    ...queryParams,
  });
  const url = `${window.location.origin}/redirect?${params.toString()}`;
  if (isIOS()) {
    window.location.href = `x-safari-${url}`;
    return true;
  }

  if (/android/i.test(window.navigator.userAgent)) {
    window.location.href = `intent:${url}#Intent;end`;
    return true;
  }
  return false;
}

export function storeRedirectData(
  path: string,
  followOnSuccess: boolean,
  queryParams?: Record<string, string | undefined>,
) {
  localStorage.setItem(REDIRECT_PATH_LOCAL_STORAGE_KEY, path);
  localStorage.setItem(
    REDIRECT_ACTION_LOCAL_STORAGE_KEY,
    followOnSuccess ? "follow" : "login",
  );
  if (queryParams) {
    localStorage.setItem(
      REDIRECT_QUERY_PARAMS_LOCAL_STORAGE_KEY,
      JSON.stringify(queryParams),
    );
  }
}

export function getRedirectData(): RedirectData | null {
  const path = localStorage.getItem(REDIRECT_PATH_LOCAL_STORAGE_KEY);
  const action = localStorage.getItem(REDIRECT_ACTION_LOCAL_STORAGE_KEY);
  const queryParams = localStorage.getItem(
    REDIRECT_QUERY_PARAMS_LOCAL_STORAGE_KEY,
  );
  if (path) {
    const redirectData: RedirectData = {
      path,
      action: (action as RedirectAction) ?? "login",
      queryParams: queryParams ? JSON.parse(queryParams) : {},
    };
    clearRedirectData();
    return redirectData;
  }
  return null;
}

function clearRedirectData() {
  localStorage.removeItem(REDIRECT_PATH_LOCAL_STORAGE_KEY);
  localStorage.removeItem(REDIRECT_ACTION_LOCAL_STORAGE_KEY);
  localStorage.removeItem(REDIRECT_QUERY_PARAMS_LOCAL_STORAGE_KEY);
}
